import { useCallback, useEffect, useState } from 'react';
import { storage } from '@hypneuronerd/storage';
import { qualities } from '../data/qualities';
import { pickIndices, todayKey } from '../lib/dates';

const HISTORY_KEY = 'daily-word:history';
const TODAY_KEY = 'daily-word:today-locked';
const OFFERED_KEY = 'daily-word:offered-today';

export interface DayRecord {
  date: string;
  word: string;
  chosenAt: string;
  reflection?: string;
  reflectedAt?: string;
}

interface LockedToday {
  date: string;
  word: string;
  chosenAt: string;
}

interface OfferedToday {
  date: string;
  /** Either a date seed (initial offering) or an incrementing refresh count. */
  seed: string;
  words: string[];
}

export function useDailyWord() {
  const [today] = useState(() => todayKey());
  const [locked, setLocked] = useState<LockedToday | null>(() => {
    const v = storage.get<LockedToday | null>(TODAY_KEY, null);
    if (v && v.date === todayKey()) return v;
    return null;
  });
  const [offered, setOffered] = useState<OfferedToday>(() => {
    const v = storage.get<OfferedToday | null>(OFFERED_KEY, null);
    if (v && v.date === todayKey()) return v;
    const seed = todayKey();
    const words = pickIndices(qualities.length, 3, seed).map(
      (i) => qualities[i]!.word
    );
    const fresh: OfferedToday = { date: todayKey(), seed, words };
    storage.set(OFFERED_KEY, fresh);
    return fresh;
  });
  const [history, setHistory] = useState<DayRecord[]>(() =>
    storage.get<DayRecord[]>(HISTORY_KEY, [])
  );

  /** Pick or accept a quality as today's word. */
  const choose = useCallback(
    (word: string) => {
      if (!qualities.find((q) => q.word === word)) return;
      const next: LockedToday = {
        date: today,
        word,
        chosenAt: new Date().toISOString(),
      };
      storage.set(TODAY_KEY, next);
      setLocked(next);
    },
    [today]
  );

  /** Reshuffle the triptych with new entropy. */
  const refreshTriptych = useCallback(() => {
    const seed = `${todayKey()}-${Date.now()}-${Math.random()}`;
    const words = pickIndices(qualities.length, 3, seed).map(
      (i) => qualities[i]!.word
    );
    const fresh: OfferedToday = { date: todayKey(), seed, words };
    storage.set(OFFERED_KEY, fresh);
    setOffered(fresh);
  }, []);

  /** Save (or update) the evening reflection for today's word. */
  const saveReflection = useCallback((text: string) => {
    setLocked((prev) => {
      if (!prev) return prev;
      const updated: LockedToday = { ...prev };
      storage.set(TODAY_KEY, updated);
      // Also stash in history-in-progress
      const trimmed = text.trim();
      const reflectedAt = trimmed.length ? new Date().toISOString() : undefined;

      setHistory((h) => {
        const idx = h.findIndex((r) => r.date === prev.date);
        const record: DayRecord = {
          date: prev.date,
          word: prev.word,
          chosenAt: prev.chosenAt,
          reflection: trimmed.length ? trimmed : undefined,
          reflectedAt,
        };
        const next = idx >= 0 ? [...h.slice(0, idx), record, ...h.slice(idx + 1)] : [record, ...h];
        storage.set(HISTORY_KEY, next);
        return next;
      });

      return updated;
    });
  }, []);

  /** Sync any cross-tab updates. */
  useEffect(() => {
    function onStorage(ev: StorageEvent) {
      if (!ev.key) return;
      if (ev.key.endsWith(TODAY_KEY)) {
        const v = storage.get<LockedToday | null>(TODAY_KEY, null);
        if (v && v.date === today) setLocked(v);
      }
      if (ev.key.endsWith(HISTORY_KEY)) {
        setHistory(storage.get<DayRecord[]>(HISTORY_KEY, []));
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [today]);

  const quality = locked
    ? qualities.find((q) => q.word === locked.word) ?? null
    : null;
  const todayReflection = locked
    ? history.find((r) => r.date === locked.date)?.reflection
    : undefined;

  return {
    today,
    locked,
    quality,
    offered: offered.words,
    history,
    todayReflection,
    choose,
    refreshTriptych,
    saveReflection,
  };
}
